import Button from "@mui/material/Button";
import ConfirmationDialog from "controls/global/dialogs/confirmation-dialog";
import { useSignatureDefaultDialogProvider } from "pages/signatures/providers/signature-defaults/SignatureDefaultDialogProvider";
import React, {
  useState,
  useEffect,
  useMemo,
} from "react";
import {
  FormProvider,
  useForm,
  useWatch,
} from "react-hook-form";
import { useSignatureDefaults } from "utils/context/SignatureDefaultsContext";
import { v4 as uuidv4 } from "uuid";
import DefaultInfo from "./default-info";
import SignatureInfo from "./signature-info";
import { useGlobalAccess } from "utils/context/GlobalAccessContext";
import { showToast } from "controls/global/stewart-toast/showToast";
import { UIConstants } from "utils/data/enum";
import useSignature from "utils/custom-hooks/useSignature";

const DUPLICATE_MESSAGE =
  "A default signature for this Agency Location already exists. Please select a different location";

const WARNNING_MESSAGE =
  "This default effects all users at this agency. Are you sure you want to revise it?";

type Dialog = {
  open: boolean;
  message?: string;
  df?: any;
};

type Props = {
  saveRef: React.MutableRefObject<HTMLButtonElement | null>;
  refreshPage?: () => void; 
};


const getLocation = (df: any) => {
  const locationArray = Array.isArray(df.location)
    ? df.location
    : [df.location];

    if(locationArray.length > 1 && locationArray.some((loc: any) => loc.value === "All")) {
      return { locationValue: "All", locationText: "All Agency Locations" };
    }

  const locationValue = locationArray.filter((loc: any) => loc.value).map((loc: any) => loc.value).join("|");
  const locationText = locationArray.filter((loc: any) => loc.text).map((loc: any) => loc.text).join("|");

  return { locationValue, locationText };
};


const SignatureDefaultForm = ({ saveRef, refreshPage }: Props) => {
  const [duplicateDialog, setDuplicateDialog] = useState<Dialog>({
    open: false,
  });
  const [warningDialog, setWarningDialog] = useState<Dialog>({
    open: false,
  });
  const [{ defaults, initialDefault, agencyWideLevelCode }, { saveDefault, resetState }] =
    useSignatureDefaults();
  const [{ selectedAgency: globalAccessAgency }] = useGlobalAccess();
  const { refreshAllDefaultSignaturesStateContext } = useSignature();

  const {
    mode,
    cancelDialog,
    updateSaveEnabled,
    hasUserInteracted,
  } = useSignatureDefaultDialogProvider();

  const methods = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      ...initialDefault,
      agency: initialDefault.agency.value,
      location: mode === "ADD" ? [] : initialDefault.location,
    },
  });

  const { handleSubmit, control } = methods;

  const [signatureImage, agency, state, location, level] = useWatch({
    control,
    name: ["signatureImage", "agency", "state", "location", "level"],
  });

  const checkForDuplicate = (agency: string, state: string, location: string, level: string, signatureImageId: number) => {
    return defaults.some(d =>
      d.agencyID === agency &&
      d.stateCode === state &&
      d.locationUniqueIDs === location &&
      d.signatureLevelTypeCode === level &&
      d.signatureImageID === signatureImageId
    );
  };

  const submitForm = async (df: any) => {
    const signatureImageId = Number(df.signatureImage.value);
    const { locationValue, locationText } = getLocation(df);

    let isDuplicate = checkForDuplicate(df.agency, df.state.value, mode === "ADD" ? locationValue : df.location.value, df.level.value, signatureImageId);

    if (!isDuplicate) {
      const data = {
        key: df.key,
        existingUserAgencySignatureDefaultIDs: initialDefault.existingUserAgencySignatureDefaultIDs,
        agencyID: df.agency,
        agencyName: df.agency.text,
        stateCode: df.state.value,
        state: df.state.text,
        locationUniqueIDs: mode === "ADD" ? locationValue : df.location.value,
        locationDisplayName: mode === "ADD" ? locationText : df.location.text,
        signatureLevelTypeCode: df.level.value,
        signatureLevelTypeName: df.level.text,
        signatureImageID: signatureImageId,
        signatureImageName: df.signatureImage.text,
        integrationKey: uuidv4(),
      };

      try {
        const status = await saveDefault({ ...data });
        isDuplicate = (status === 409);

        if (!isDuplicate) {
          if (status >= 200 && status < 300) {
            showToast({
              type: "success",
              title: mode === "EDIT" 
                ? UIConstants.UPDATE_DEFAULT_SIGNATURE_SUCCESS_MESSAGE.toString()
                : UIConstants.ADD_DEFAULT_SIGNATURE_SUCCESS_MESSAGE.toString(),
            });
            cancelDialog();
            resetState();

            // refreshAllSignaturesStateContext(globalAccessAgency[0]?.CompanyID);  // No need to refresh signatures ocntext here
            refreshAllDefaultSignaturesStateContext(globalAccessAgency[0]?.CompanyID);
            refreshPage && refreshPage();
          } else {
            showToast({
              type: "error",
              title: mode === "EDIT"
                ? UIConstants.UPDATE_DEFAULT_SIGNATURE_ERROR_MESSAGE.toString()
                : UIConstants.ADD_DEFAULT_SIGNATURE_ERROR_MESSAGE.toString(),
            });
          }
          return;
        }
      } catch (error) {
        showToast({
          type: "error",
          title: mode === "EDIT"
            ? UIConstants.UPDATE_DEFAULT_SIGNATURE_ERROR_MESSAGE.toString()
            : UIConstants.ADD_DEFAULT_SIGNATURE_ERROR_MESSAGE.toString(),
        });
        return;
      }
    }

    if (isDuplicate) {
      setDuplicateDialog({
        open: true,
        message: DUPLICATE_MESSAGE,
      });
      return;
    }
  };

  const preSubmitForm = async (df: any) => {
    updateSaveEnabled(false);
    if (df.level.value === agencyWideLevelCode && mode === "EDIT") {
      setWarningDialog({
        open: true,
        message: WARNNING_MESSAGE,
        df: df,
      });
      return;
    }
    await submitForm(df);
  };

  const handleYes = async (df: any) => {
    await submitForm(df);
    setWarningDialog({
      open: false,
      message: "",
      df: null,
    });
  };

  const handleNo = () => {
    updateSaveEnabled(true);
    setWarningDialog({
      open: false,
      message: "",
      df: null,
    });
  };

  const handleOk = () => {
    updateSaveEnabled(true);
    setDuplicateDialog({
      open: false,
    });
  };

  const areAllFieldsFilled = () => {
    const hasSignatureImage = !!(signatureImage?.value && signatureImage?.value !== "");
    const hasAgency = !!(agency && agency !== "");
    const hasState = !!(state?.value && state?.value !== "");
    const hasLevel = !!(level?.value && level?.value !== "");
    
    let hasLocation = false;
    if (mode === "ADD") {
      if (Array.isArray(location)) {
        hasLocation = location.length > 0 && location.some((loc: any) => loc?.value && loc.value !== "");
      }
    } else {
      hasLocation = !!(!Array.isArray(location) && location?.value && location?.value !== "");
    }

    return hasSignatureImage && hasAgency && hasState && hasLocation && hasLevel;
  };

  const hasDataChanged = () => {
    if (mode === "ADD") {
      return hasUserInteracted.current;
    }

    const { locationValue } = getLocation({ location });
    
    const currentLocationParts = locationValue ? locationValue.split("|").sort() : [];
    const initialLocationParts = initialDefault.location.value ? initialDefault.location.value.split("|").sort() : [];
    const locationsMatch = 
      currentLocationParts.length === initialLocationParts.length &&
      currentLocationParts.every((val: string, idx: number) => val === initialLocationParts[idx]);

    return (
      signatureImage?.value !== initialDefault.signatureImage.value ||
      agency !== initialDefault.agency.value ||
      state?.value !== initialDefault.state.value ||
      !locationsMatch ||
      level?.value !== initialDefault.level.value
    );
  };

  const shouldEnableSave = useMemo(() => {
    const allFilled = areAllFieldsFilled();
    const dataChanged = hasDataChanged();

    return allFilled && dataChanged;
  }, [signatureImage, agency, state, location, level, mode, initialDefault]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateSaveEnabled(shouldEnableSave);
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [shouldEnableSave]);

  return (
    <>
      <FormProvider {...methods}>
        <form>
          <SignatureInfo />
          <DefaultInfo />
          <Button
            hidden
            //TODO REVIEW MUI5
            ref={saveRef}
            onClick={handleSubmit(preSubmitForm)}
          />
        </form>
      </FormProvider>
      {duplicateDialog.open && (
        <ConfirmationDialog
          isOpen={duplicateDialog.open}
          confirmationMessage={duplicateDialog.message}
          singleActionButton={true}
          singleActionButtonText="Ok"
          onYes={handleOk}
        />
      )}
      {warningDialog.open && (
        <ConfirmationDialog
          isOpen={warningDialog.open}
          confirmationMessage={warningDialog.message}
          onYes={() => handleYes(warningDialog.df)}
          onNo={handleNo}
        />
      )}
    </>
  );
};

export default SignatureDefaultForm;
